import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { EVENT } from "@/data/content";
import { TopNav } from "@/components/TopNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_STATS = {
  total: 0,
  attending: 0,
  not_attending: 0,
  invited: 0,
  responded: 0,
  pending_guests: [],
};

export default function AdminRSVPs() {
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        axios.get(`${API}/rsvp`),
        axios.get(`${API}/rsvp/stats`),
      ]);
      setRsvps(list.data || []);
      setStats(st.data || EMPTY_STATS);
      setError(null);
    } catch (e) {
      setError("Failed to load RSVPs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = rsvps.filter((r) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return (
      r.name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.notes?.toLowerCase().includes(q) ||
      r.dietary_restrictions?.toLowerCase().includes(q)
    );
  });

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const renderTableBody = () => {
    if (loading) {
      return <div className="p-12 text-center text-[#A3A3A3]">Loading…</div>;
    }
    if (error) {
      return <div className="p-12 text-center text-red-400">{error}</div>;
    }
    if (filtered.length === 0) {
      return (
        <div className="p-12 text-center text-[#A3A3A3]">
          No reservations yet.
        </div>
      );
    }
    return null;
  };

  return (
    <main
      data-testid="admin-page"
      className="min-h-[100svh] bg-[#0a0a0a] text-[#FDFBF7] px-6 md:px-12 py-12 md:py-16 pt-24 md:pt-28"
    >
      <TopNav />
      <div className="max-w-[1400px] mx-auto">
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-[#D4AF37]/20 pb-8">
          <div>
            <div className="eyebrow-line">Administration</div>
            <h1
              className="font-serif-display mt-4 leading-none"
              style={{ fontSize: "clamp(40px, 5vw, 64px)" }}
            >
              {EVENT.brand} <span className="italic text-[#D4AF37]">Reservations</span>
            </h1>
            <p className="text-[#A3A3A3] mt-2 text-sm tracking-wide">
              {EVENT.dateLong} · {EVENT.time} · {EVENT.city}
            </p>
          </div>
          <button
            onClick={fetchData}
            className="btn-ghost"
            data-testid="admin-refresh-btn"
          >
            Refresh
          </button>
        </header>

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-10">
          {[
            ["Invited", stats.invited, "admin-stat-invited"],
            ["Responded", stats.responded, "admin-stat-responded"],
            ["Attending", stats.attending, "admin-stat-attending"],
            ["Declined", stats.not_attending, "admin-stat-declined"],
          ].map(([label, value, tid]) => (
            <div
              key={label}
              data-testid={tid}
              className="border border-[#D4AF37]/20 p-6 md:p-7 bg-[#0f0f0f]"
            >
              <div className="overline">{label}</div>
              <div
                className="font-serif-display text-[#FDFBF7] mt-3"
                style={{ fontSize: "clamp(32px, 3.5vw, 48px)" }}
              >
                {value}
              </div>
            </div>
          ))}
        </section>

        {/* Pending guests */}
        {stats.pending_guests && stats.pending_guests.length > 0 && (
          <section
            data-testid="admin-pending"
            className="mt-8 border border-[#D4AF37]/20 p-6 md:p-7 bg-[#0f0f0f]"
          >
            <div className="overline">Awaiting Response</div>
            <div className="flex flex-wrap gap-2 mt-4">
              {stats.pending_guests.map((g) => (
                <span
                  key={g}
                  className="px-3 py-1.5 border border-[#D4AF37]/25 text-[#FDFBF7] text-sm font-serif-display"
                >
                  {g}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Search */}
        <div className="mt-10">
          <input
            data-testid="admin-search"
            type="search"
            placeholder="Search name, email, dietary, notes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="rpm-input max-w-[480px]"
          />
        </div>

        {/* Table */}
        <section className="mt-8 border border-[#D4AF37]/20 bg-[#0f0f0f] overflow-x-auto">
          {renderTableBody() || (
            <table className="w-full text-left" data-testid="admin-rsvp-table">
              <thead>
                <tr className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border-b border-[#D4AF37]/20">
                  <th className="px-5 py-4">Guest</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Dietary</th>
                  <th className="px-5 py-4">Notes</th>
                  <th className="px-5 py-4">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`admin-rsvp-row-${r.id}`}
                    className="border-b border-[#D4AF37]/10 hover:bg-[#141414] transition-colors"
                  >
                    <td className="px-5 py-5 font-serif-display text-lg">{r.name}</td>
                    <td className="px-5 py-5 text-[#FDFBF7]/85 text-sm">{r.email}</td>
                    <td className="px-5 py-5">
                      <span
                        className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase"
                        style={{
                          color: r.attendance === "attending" ? "#D4AF37" : "#A3A3A3",
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{
                            background:
                              r.attendance === "attending" ? "#D4AF37" : "#555",
                          }}
                        />
                        {r.attendance === "attending" ? "Attending" : "Declined"}
                      </span>
                    </td>
                    <td className="px-5 py-5 text-[#A3A3A3] text-sm max-w-[220px]">
                      {r.dietary_restrictions || "—"}
                    </td>
                    <td className="px-5 py-5 text-[#A3A3A3] text-sm max-w-[280px]">
                      {r.notes || "—"}
                    </td>
                    <td className="px-5 py-5 text-[#7a7a7a] text-xs">
                      {fmtDate(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <p className="text-[#7a7a7a] text-xs tracking-[0.25em] uppercase mt-8">
          Hidden URL · do not share
        </p>
      </div>
    </main>
  );
}
